/**
 * "This might be someone you already have" (Plan D12).
 *
 * Shown above everything else on the profile, because the outcome it exists to
 * prevent is a duplicate being activated by habit — a warning below the fold
 * would arrive after the decision.
 *
 * **Amber, not red, and worded as a question.** Section 3.9 is explicit that
 * shared contact details are legitimate: households share a phone, and families
 * share an inbox. So the flag says what matched and shows the other record; it
 * does not say "this is a duplicate", because quite often it is not, and a
 * warning that cries wolf is one staff learn to click past.
 */

import { Link } from 'react-router-dom';
import type { RegistrationAwaitingDecision, Volunteer } from '@parinaam-vms/shared';
import { useVolunteerRegistrations } from '../../hooks/useVolunteers';
import { formatDate, humaniseCode } from '../../utils/format';
import styles from './DuplicateWarning.module.css';

export interface DuplicateWarningProps {
  volunteer: Volunteer;
}

export function DuplicateWarning({ volunteer }: DuplicateWarningProps) {
  const query = useVolunteerRegistrations(volunteer.volunteer_id);
  const registrations = query.data ?? [];

  /**
   * A repeat submission is flagged against the volunteer it reused — which is
   * *this* volunteer. Left in, the panel would announce that this record is a
   * possible duplicate of itself, which is both true and useless.
   *
   * The genuine warning is a match against somebody else. The repeat is covered
   * by the line below it.
   */
  const flagged = registrations.filter(
    (row) => row.possible_duplicate_of && row.possible_duplicate_of !== volunteer.volunteer_id
  );
  const repeats = registrations.length > 1;

  // Nothing to say: a staff-entered record, or a submission that matched nobody.
  if (flagged.length === 0 && !repeats) return null;

  return (
    <div className={styles.warning} role="status">
      {flagged.length > 0 && <MatchedSomebody match={flagged[0]!} />}

      {repeats && (
        <p className={styles.line}>
          <strong>This person submitted the form {registrations.length} times.</strong> The repeats
          did not create extra records — they are all on this one. Submitted{' '}
          {registrations.map((row) => formatDate(row.submitted_at)).join(', ')}.
        </p>
      )}
    </div>
  );
}

/**
 * What the two records have in common, said in full.
 *
 * Both values when both match, because that pair is the whole question: one
 * address and one number between two names is a household, and staff cannot
 * tell that from "they share a phone or an email address".
 *
 * Neither matching is a real state and worth saying plainly — it means somebody
 * has edited one of the records since the submission arrived, so the flag is
 * now about details that no longer agree.
 */
function sharedDetails(match: RegistrationAwaitingDecision): string {
  const email = match.shares_email ? match.shared_email : null;
  const phone = match.shares_phone ? match.shared_phone : null;

  if (email && phone) {
    return `They share an email address (${email}) and a phone number (${phone}).`;
  }
  if (email) return `They share an email address (${email}).`;
  if (phone) return `They share a phone number (${phone}).`;

  return 'The details no longer match.';
}

function MatchedSomebody({ match }: { match: RegistrationAwaitingDecision }) {
  const how = match.duplicate_of_status === null ? null : humaniseCode(match.duplicate_of_status);

  return (
    <p className={styles.line}>
      <strong>Possible duplicate.</strong> When this registration arrived it matched{' '}
      <Link to={`/volunteers/${match.possible_duplicate_of}`}>
        {match.duplicate_of_name} ({match.duplicate_of_code})
      </Link>
      {how ? `, who is ${how.toLowerCase()}` : ''}. {sharedDetails(match)} Open both before
      deciding — sharing these is legitimate and common with Parinaam volunteers, so this may be
      two people in one household rather than one person twice.
    </p>
  );
}
